import {
    resetFinancialAssistantConversation,
    sendFinancialAssistantMessage,
} from '@/lib/api/financial/financial-assistant-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useRef, useState } from 'react'
import { MdAutoAwesome, MdClose, MdRefresh, MdSend } from 'react-icons/md'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toast } from 'react-toastify'

interface ChatMessage {
    role: 'user' | 'assistant'
    content: string
}

interface FinancialAssistantPanelProps {
    open: boolean
    onClose: () => void
}

const SUGGESTIONS = [
    'Como foi o mês passado? Receita, custo e lucro, comparando com o mês anterior.',
    'Quanto devemos em repasses vencidos, e para quais parceiros principalmente?',
    'Quanto recebemos este ano por origem: consulta, exame, procedimento e avulso?',
    'Qual a margem dos procedimentos este mês, e quais itens mais venderam?',
    'Quanto temos a receber em aberto por faixa de atraso, somando todas as filiais?',
]

/**
 * A resposta vem em markdown, com tabelas (remark-gfm). O preflight do Tailwind zera
 * o estilo de tabela e de lista, então cada elemento ganha a classe aqui — sem isto a
 * tabela saía como texto corrido.
 */
const MARKDOWN_COMPONENTS = {
    p: (props: any) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
    ul: (props: any) => <ul className="mb-2 list-disc space-y-1 pl-5" {...props} />,
    ol: (props: any) => <ol className="mb-2 list-decimal space-y-1 pl-5" {...props} />,
    strong: (props: any) => <strong className="font-semibold" {...props} />,
    h1: (props: any) => <h3 className="mb-2 mt-3 font-semibold" {...props} />,
    h2: (props: any) => <h3 className="mb-2 mt-3 font-semibold" {...props} />,
    h3: (props: any) => <h3 className="mb-2 mt-3 font-semibold" {...props} />,
    table: (props: any) => (
        <div className="my-2 overflow-x-auto rounded-lg border border-base-300">
            <table className="table table-xs" {...props} />
        </div>
    ),
    th: (props: any) => <th className="whitespace-nowrap bg-base-200/60 text-xs" {...props} />,
    td: (props: any) => <td className="whitespace-nowrap text-xs" {...props} />,
}

export function FinancialAssistantPanel({ open, onClose }: FinancialAssistantPanelProps) {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [conversationId, setConversationId] = useState<string | undefined>()

    const bottomRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const { user } = useAuth()

    /**
     * A conversa é retomada de onde parou, por usuário e por filial — o backend amarra
     * a conversa a (usuário, empresa). Mesmo desenho do assistente do catálogo.
     */
    const storageKey = `financial-assistant:${user?.id ?? 'anon'}:${user?.companyId ?? 'none'}`

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey)
            const parsed = saved ? JSON.parse(saved) : null
            setConversationId(parsed?.conversationId)
            setMessages(Array.isArray(parsed?.messages) ? parsed.messages : [])
        } catch {
            setConversationId(undefined)
            setMessages([])
        }
        setInput('')
    }, [storageKey])

    useEffect(() => {
        try {
            if (!conversationId && messages.length === 0) {
                localStorage.removeItem(storageKey)
                return
            }
            localStorage.setItem(storageKey, JSON.stringify({ conversationId, messages }))
        } catch {
            // cota do localStorage estourada: a conversa segue viva no servidor
        }
    }, [storageKey, conversationId, messages])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, loading])

    useEffect(() => {
        if (open) inputRef.current?.focus()
    }, [open])

    async function handleReset() {
        const response = await resetFinancialAssistantConversation(conversationId)
        setConversationId(response.result?.conversationId)
        setMessages([])
        setInput('')
    }

    async function handleSend(text?: string) {
        const message = (text ?? input).trim()
        if (!message || loading) return

        setInput('')
        setMessages(current => [...current, { role: 'user', content: message }])
        setLoading(true)

        try {
            const response = await sendFinancialAssistantMessage({ message, conversationId })
            const data = response.result
            if (!data) {
                toast.error(response.error || 'Não consegui falar com o assistente')
                return
            }
            setConversationId(data.conversationId)
            setMessages(current => [...current, { role: 'assistant', content: data.reply }])
        } catch {
            toast.error('Erro ao falar com o assistente')
        } finally {
            setLoading(false)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

            <aside className="relative flex h-full w-full max-w-2xl flex-col bg-base-100 shadow-2xl">
                <header className="flex items-center justify-between gap-3 border-b border-base-300/50 p-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/70 shadow-lg">
                            <MdAutoAwesome className="text-2xl text-white" />
                        </div>
                        <div>
                            <h2 className="font-bold text-base-content">Assistente financeiro</h2>
                            <p className="text-xs text-base-content/60">
                                Pergunte sobre caixa, produção, repasses e inadimplência
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {messages.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1"
                                onClick={() => handleReset()}
                                disabled={loading}
                                title="Esquecer o que foi conversado e começar do zero"
                            >
                                <MdRefresh className="text-lg" />
                                <span className="hidden sm:inline">Nova conversa</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-circle"
                            onClick={onClose}
                            aria-label="Fechar assistente"
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>
                </header>

                <div className="flex-1 space-y-4 overflow-y-auto p-4">
                    {messages.length === 0 && (
                        <div className="space-y-3">
                            <p className="text-sm text-base-content/70">
                                Faça a pergunta como faria ao financeiro. O assistente consulta os
                                dados na hora e diz o período e os filtros que usou.
                            </p>
                            {SUGGESTIONS.map(suggestion => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    className="btn btn-outline btn-sm h-auto w-full justify-start whitespace-normal py-2 text-left font-normal"
                                    onClick={() => handleSend(suggestion)}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}

                    {messages.map((message, index) => (
                        <div
                            key={index}
                            className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                        >
                            <div
                                className={`min-w-0 max-w-[90%] rounded-2xl px-4 py-2.5 text-sm ${
                                    message.role === 'user'
                                        ? 'bg-primary text-primary-content'
                                        : 'bg-base-200 text-base-content'
                                }`}
                            >
                                {message.role === 'user' ? (
                                    <p className="whitespace-pre-wrap">{message.content}</p>
                                ) : (
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                                        {message.content}
                                    </ReactMarkdown>
                                )}
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="flex justify-start">
                            <div className="flex items-center gap-2 rounded-2xl bg-base-200 px-4 py-3 text-xs text-base-content/60">
                                <span className="loading loading-dots loading-sm" />
                                Consultando os dados...
                            </div>
                        </div>
                    )}

                    <div ref={bottomRef} />
                </div>

                <footer className="border-t border-base-300/50 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            ref={inputRef}
                            className="textarea textarea-bordered flex-1 resize-none"
                            rows={2}
                            placeholder="Ex.: quanto recebemos de exames em agosto, por forma de pagamento?"
                            value={input}
                            disabled={loading}
                            onChange={event => setInput(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    handleSend()
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary btn-square"
                            disabled={loading || !input.trim()}
                            onClick={() => handleSend()}
                            aria-label="Enviar"
                        >
                            <MdSend className="text-xl" />
                        </button>
                    </div>
                    <p className="mt-2 text-xs text-base-content/50">
                        Só consulta: o assistente não altera nenhum lançamento.
                    </p>
                </footer>
            </aside>
        </div>
    )
}
